import type { AdminOrgDto } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CodeBox } from './CodeBox';
import { OwnerCodeDialog } from './OwnerCodeDialog';
import { InfoRow, SectionCard, Value } from './SectionCard';
import { rotateOrgMemberCode, useAdminOrgMemberCode } from '../../../api/admin-orgs';
import { ConfirmationDialog } from '../../../components/feedback';
import { formatDate, formatDateTime } from '../../../utils/format';
import { ownerCodeState } from '../_lib/ownerCodeState';
import { useOrgWrite } from '../_lib/useOrgWrite';
import { useOwnerCode } from '../_lib/useOwnerCode';

/**
 * «Коды» (AUT-403), after `companies-detail.html?tab=codes`.
 *
 * Owner code: never stored, so the card shows its STATE (used / pending /
 * none) and a masked value; «Выпустить / Перевыпустить» (behind a confirm)
 * shows the fresh plaintext ONCE in {@link OwnerCodeDialog}, held only in
 * component state and dropped on close.
 *
 * Member code: re-derived by the server on every read, so it is shown in
 * full with «Скопировать»; «Сгенерировать новый» (confirm) rotates it — the
 * old one answers 410, members stay. A terminated company has no live
 * codes: both cards say so and the member-code read is not made.
 */
const OwnerCodeCard = ({ org }: { org: AdminOrgDto }): ReactElement => {
  const { t } = useTranslation();
  const owner = useOwnerCode();
  const [confirming, setConfirming] = useState(false);
  const state = ownerCodeState(org);
  const ns = 'companies.codes.owner';

  const issue = (): void => {
    void owner.issue(org.id).then(() => setConfirming(false));
  };

  const action =
    org.status === 'terminated' ? null : (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button size="small" variant="outlined" onClick={() => setConfirming(true)}>
          {t(state === 'none' ? `${ns}.issue` : `${ns}.reissue`)}
        </Button>
        <Typography variant="caption" color="text.secondary">
          {t(`${ns}.actionHint.${state}`)}
        </Typography>
      </Box>
    );

  return (
    <SectionCard title={t(`${ns}.title`)} sub={t(`${ns}.kind`)}>
      <InfoRow label={t(`${ns}.stateLabel`)}>
        <Value>{t(`${ns}.state.${state}`)}</Value>
      </InfoRow>
      {org.pendingOwnerCode ? (
        <InfoRow label={t(`${ns}.issuedAt`)}>
          <Value>{formatDateTime(org.pendingOwnerCode.issuedAt)}</Value>
        </InfoRow>
      ) : null}
      <InfoRow label={t(`${ns}.owner`)}>
        <Value>{org.owner?.name ?? t(`${ns}.ownerNone`)}</Value>
      </InfoRow>
      {state !== 'none' ? (
        <CodeBox code="" masked maskedHint={t(`${ns}.maskedHint.${state}`)} />
      ) : null}
      {action}
      <ConfirmationDialog
        open={confirming}
        variant="warning"
        title={t(`${ns}.dialog.title.${state}`)}
        description={t(org.owner ? `${ns}.dialog.bodyOwner` : `${ns}.dialog.body`, {
          name: org.owner?.name ?? '',
        })}
        confirmLabel={t(`${ns}.dialog.confirm`)}
        loading={owner.busy}
        onConfirm={issue}
        onCancel={() => setConfirming(false)}
      />
      <OwnerCodeDialog
        code={owner.code}
        companyName={org.name}
        variant="issued"
        reissuing={owner.busy}
        onReissue={() => void owner.issue(org.id)}
        onClose={owner.clear}
      />
    </SectionCard>
  );
};

const MemberCodeCard = ({ org }: { org: AdminOrgDto }): ReactElement => {
  const { t } = useTranslation();
  const live = org.status !== 'terminated';
  const query = useAdminOrgMemberCode(org.id, live);
  const write = useOrgWrite();
  const [confirming, setConfirming] = useState(false);
  const ns = 'companies.codes.member';

  const rotate = (): void => {
    void write
      .run(() => rotateOrgMemberCode(org.id), `${ns}.rotated`)
      .then(() => setConfirming(false));
  };

  return (
    <SectionCard title={t(`${ns}.title`)} sub={t(`${ns}.kind`)}>
      {!live ? <Alert severity="info">{t('companies.codes.terminated')}</Alert> : null}
      {live && query.isLoading ? <CircularProgress size={24} /> : null}
      {live && query.error && !query.data ? (
        <Alert severity="warning">{t(`${ns}.loadError`)}</Alert>
      ) : null}
      {query.data ? (
        <>
          <InfoRow label={t(`${ns}.issuedAt`)}>
            <Value>{formatDate(query.data.issuedAt)}</Value>
          </InfoRow>
          <InfoRow label={t(`${ns}.used`)}>
            <Value>
              {t(`${ns}.usedValue`, {
                used: query.data.usedCount,
                free: Math.max(0, org.maxMembers - org.activeMembers),
                max: org.maxMembers,
              })}
            </Value>
          </InfoRow>
          <CodeBox code={query.data.code} testId="company-member-code-value" />
        </>
      ) : null}
      {live && (query.data || query.error) ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setConfirming(true)}
            data-testid="company-member-code-rotate"
          >
            {t(`${ns}.rotate`)}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t(`${ns}.rotateHint`)}
          </Typography>
        </Box>
      ) : null}
      <ConfirmationDialog
        open={confirming}
        variant="warning"
        title={t(`${ns}.dialog.title`)}
        description={t(`${ns}.dialog.body`)}
        confirmLabel={t(`${ns}.dialog.confirm`)}
        loading={write.busy}
        testId="company-member-code-rotate"
        onConfirm={rotate}
        onCancel={() => setConfirming(false)}
      />
    </SectionCard>
  );
};

export const CodesTab = ({ org }: { org: AdminOrgDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
        <OwnerCodeCard org={org} />
        <MemberCodeCard org={org} />
      </Box>
      <Typography variant="caption" color="text.secondary">
        {t('companies.codes.note')}
      </Typography>
    </Box>
  );
};
